import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { movies, getFeaturedMovies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';
import { faqList } from '@/mocks/faq';

const baseUrl = 'https://qiqicinema.com';

export default function Home() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(true);
  }, []);

  const featuredMovies = getFeaturedMovies();
  const latestMovies = movies.slice(0, 8);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '琪琪影院',
        url: baseUrl,
        logo: `${baseUrl}/logo.png`,
        description: '琪琪影院提供高清影视蓝光画质在线观看，不缓冲播放技术带来流畅观影体验，超清视频免费看，海量影视资源即时点播。',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@qiqicinema.com',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebSite',
        name: '琪琪影院',
        url: baseUrl,
        potentialAction: {
          '@type': 'SearchAction',
          'target': `${baseUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '琪琪影院 - 高清影视蓝光画质在线观看',
        url: baseUrl,
        isPartOf: { '@type': 'WebSite', name: '琪琪影院', url: baseUrl },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqList.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="琪琪影院 - 高清影视蓝光画质在线观看 | 不缓冲播放流畅观影超清视频免费看"
        description="琪琪影院提供高清影视蓝光画质在线观看，不缓冲播放技术带来流畅观影体验，超清视频免费看，海量影视资源即时点播。"
        keywords="高清影视,蓝光画质,不缓冲播放,流畅观影,超清视频,琪琪影院,在线观看,免费高清电影,高清电视剧,无卡顿播放"
        canonicalUrl={baseUrl}
        ogType="website"
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20cinematic%20atmosphere%20with%20warm%20golden%20spotlights%20illuminating%20a%20dark%20theater%20stage%2C%20floating%20dust%20particles%20in%20the%20light%20beam%2C%20rich%20red%20velvet%20curtains%20on%20the%20sides%2C%20elegant%20and%20artistic%20composition%2C%20warm%20dramatic%20lighting%2C%20abstract%20cinematic%20background&width=1800&height=900&seq=hero-bg-cinema&orientation=landscape"
            alt="琪琪影院 - 高清影视在线观看"
            title="琪琪影院 - 高清影视在线观看"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className={`text-center w-full max-w-3xl transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight">
                海量高清影视<span className="text-primary-400">免费在线观看</span>
              </h1>
              <p className="mt-4 md:mt-6 text-sm md:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
                蓝光画质 · 不缓冲播放 · 流畅观影体验 · 超清视频即时点播
              </p>
              <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/全部"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-movie-line text-lg"></i>
                  开始观影
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-md hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/30 text-sm"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { value: '10万+', label: '影视资源' },
                { value: '120万+', label: '日活用户' },
                { value: '98.5%', label: '好评率' },
                { value: '蓝光4K', label: '最高画质' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-xl md:text-2xl font-bold text-primary-500 font-heading">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Movies */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  精选推荐
                </h2>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">本周最受欢迎的影视作品</p>
              </div>
              <Link
                to="/category/全部"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
              {featuredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="text-center mb-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">分类浏览</h2>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">按类型快速找到你想看的影片</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.name}`}
                  className="group flex flex-col items-center gap-3 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Movies */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
                  <i className="ri-timer-flash-line text-accent-500 mr-2"></i>
                  最新上线
                </h2>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">近期更新的热门影视</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4" data-product-shop>
              {latestMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="text-center mb-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">常见问题</h2>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">关于琪琪影院的常见疑问解答</p>
            </div>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqList.map((faq, index) => (
                <div key={index} className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform text-lg ${faqOpen === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {faqOpen === index && (
                    <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20home%20theater%20setup%20with%20soft%20ambient%20lighting%2C%20comfortable%20seating%2C%20large%20screen%20with%20subtle%20glow%2C%20cozy%20and%20inviting%20mood%2C%20cinematic%20aesthetic%2C%20dark%20warm%20tones&width=1800&height=600&seq=cta-bg-cinema&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 to-foreground-950/60"></div>
          <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-white font-heading">准备好开始你的观影之旅了吗？</h2>
            <p className="mt-3 text-sm text-white/70 max-w-md mx-auto">海量高清影视资源，蓝光画质流畅播放，完全免费，即刻体验！</p>
            <Link
              to="/category/全部"
              className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即免费观影
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}